import React, {useRef, useId, useEffect, lazy, Suspense} from 'react'
import PropTypes from 'prop-types'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {Box, Stack, Text} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import breakpoints from '@app/theme/foundations/breakpoints'
import {isHydrated} from '../../utils/utils'

const Slider = lazy(() => import('react-slick'))

/**
 * Carousel is a reusable React component that renders a customizable, accessible carousel (slider) for displaying a collection of child elements.
 *
 * This component supports responsive breakpoints, custom settings, and accessibility enhancements for screen readers and keyboard navigation.
 * It leverages react-slick for the slider functionality, with a fallback layout for server-side rendering or when the slider library is not loaded.
 *
 * Accessibility features include:
 * - Proper ARIA roles and labels for the carousel region and slides.
 * - Dynamic aria-labels for each slide, indicating their position in the carousel.
 * - Management of tab focus for interactive elements within slides, ensuring only visible slides are focusable.
 *
 * Props:
 * @param {Object} settings - Configuration options for the underlying slider (react-slick).
 * @param {ReactNode[]} children - The slide content to display within the carousel.
 * @param {string} label - Accessible label for the carousel region.
 * @param {function} setSwiper - Callback to set the swiper/slider reference.
 * @param {Object} nbSlidesToShowByBreakpoint - Mapping of breakpoints to the number of slides to show.
 * @param {Object} props - Additional props passed to the slider component.
 *
 * The component automatically applies accessibility attributes and updates them on slider initialization and after slide changes.
 * It also provides a visually hidden description for screen readers, explaining carousel navigation.
 */

const Carousel = ({
    settings,
    children,
    label,
    setSwiper,
    nbSlidesToShowByBreakpoint,
    smallTileRatio,
    ...props
}) => {
    const intl = useIntl()
    const titleId = useId()
    let swiperRef = useRef()

    const breakpointsEntries = Object.entries(breakpoints)

    /**
     * Enhances accessibility for a carousel component by setting appropriate ARIA roles and attributes
     * on the carousel container and its slides. This function:
     *
     * - Assigns the 'list' role to the carousel's container element.
     * - Iterates over each slide, assigning 'listitem' and 'group' roles, and sets descriptive ARIA labels
     *   for both the product and the slide's position within the carousel.
     * - Dynamically updates the 'aria-label' of each slide to indicate its order out of the total number of slides.
     * - Adjusts the tabIndex of interactive elements (buttons and links) within each slide to ensure that only
     *   elements in visible (active) slides are focusable, addressing a known accessibility issue in the react-slick library.
     *
     * This function relies on the presence of a `swiperRef` object referencing the carousel, an `intl` object for
     * internationalized messages, and a `children` array representing the carousel slides.
     */
    const handleAccessibility = () => {
        swiperRef?.innerSlider?.list?.children[0]?.setAttribute('role', 'list')
        const slides = swiperRef?.innerSlider?.list?.querySelectorAll('.slick-slide')

        const productAriaLabel = intl.formatMessage({
            defaultMessage: 'Product',
            id: 'add_to_cart_modal.aria_label.product'
        })

        if (slides) {
            slides.forEach((slide) => {
                // Set A11y attributes on slides
                const slideIndex = parseInt(slide?.dataset?.index)
                slide.setAttribute('role', 'listitem')
                slide.children[0]?.setAttribute('role', 'group')
                slide.children[0]?.setAttribute('aria-label', productAriaLabel)
                if (slideIndex >= 0 && slideIndex < children.length) {
                    slide.setAttribute(
                        'aria-label',
                        intl.formatMessage(
                            {
                                id: 'carousel.slide.label',
                                defaultMessage: 'slide {slideNumber} out of {totalSlides}'
                            },
                            {
                                slideNumber: parseInt(slide?.dataset?.index) + 1,
                                totalSlides: children.length
                            }
                        )
                    )
                }
                // We have to make links of non visible slides non focusable
                // because there is a bug in react-slick library
                const slideLinks = slide?.querySelectorAll('button, a')
                if (slideLinks && slideLinks.length > 0) {
                    if (slide?.classList?.contains('slick-active')) {
                        slideLinks?.forEach((link) => {
                            link.tabIndex = 0
                        })
                    } else {
                        slideLinks?.forEach((link) => {
                            link.tabIndex = -1
                        })
                    }
                }
            })
        }
    }

    useEffect(() => {
        handleAccessibility()
    }, [swiperRef])

    const carouselSettings = {
        ...settings,
        afterChange: () => {
            settings.afterChange && settings.afterChange()
            handleAccessibility()
        }
    }

    const sliderClass =
        settings?.slidesToShow > 1 && settings?.centerMode && !settings?.infinite
            ? 'slick-translate'
            : ''

    // Fallback for the slider with same appearance
    const fallbackComponent = (
        <Stack
            direction="row"
            spacing={0}
            wrap="nowrap"
            className="slick-slider"
            width={smallTileRatio ? 'fit-content' : 'auto'}
        >
            {React.Children.map(children, (child, index) => {
                const flexValue = smallTileRatio
                    ? index % 2 === 0
                        ? 1
                        : smallTileRatio
                    : '0 0 auto'
                const widthValue = breakpointsEntries.map(([key]) => {
                    if (smallTileRatio) {
                        const slidesToShow = nbSlidesToShowByBreakpoint[key]
                        const largeTileRatio = 1
                        let totalRatio = 0
                        if (smallTileRatio) {
                            for (let i = 0; i < slidesToShow; i++) {
                                totalRatio += i % 2 === 0 ? largeTileRatio : smallTileRatio
                            }
                        } else {
                            smallTileRatio = 1
                            totalRatio = 1
                        }

                        // Calculate the width for the current index
                        return `${Math.round(
                            100 *
                                (index % 2 === 0
                                    ? largeTileRatio / totalRatio
                                    : smallTileRatio / totalRatio)
                        )}vw`
                    } else {
                        const slidesToShow = nbSlidesToShowByBreakpoint[key]
                        return `calc(100% / ${slidesToShow})`
                    }
                })

                return (
                    <Box
                        key={index}
                        flex={flexValue}
                        width={widthValue}
                        style={{scrollSnapAlign: 'start'}}
                    >
                        {child}
                    </Box>
                )
            })}
        </Stack>
    )

    return (
        <Box
            css={[Carousel.css(settings?.slidesToShow)]}
            aria-describedby={titleId}
            role="region"
            aria-label={label}
            overflow={'hidden'}
        >
            {/* Title for screen readers */}
            <Text className="sr-only" id={titleId}>
                {intl.formatMessage({
                    id: 'carousel.description',
                    defaultMessage:
                        'This is a carousel, you can use it by navigating through the slides using the arrows or swipe gestures.'
                })}
            </Text>
            {/* Slider with fallback in case of server rendering or if react-slick is not loaded yet */}
            {isHydrated() ? (
                <Suspense fallback={fallbackComponent}>
                    <Slider
                        ref={(slider) => {
                            swiperRef = slider
                            setSwiper && setSwiper(slider)
                        }}
                        {...carouselSettings}
                        {...props}
                        className={sliderClass}
                        onInit={() => {
                            settings.onInit && settings.onInit()
                            handleAccessibility()
                        }}
                        onReInit={() => {
                            settings.onReInit && settings.onReInit()
                            handleAccessibility()
                        }}
                    >
                        {children}
                    </Slider>
                </Suspense>
            ) : (
                fallbackComponent
            )}
        </Box>
    )
}

Carousel.css = (nbSlidesToShow) => css`
    .slick-translate .slick-track {
        left: calc(-100% / ${nbSlidesToShow});
    }
`

Carousel.propTypes = {
    settings: PropTypes.object,
    children: PropTypes.array,
    label: PropTypes.string,
    setSwiper: PropTypes.func,
    nbSlidesToShowByBreakpoint: PropTypes.object,
    smallTileRatio: PropTypes.number
}

export default Carousel
